import clsx from 'clsx';
import React from 'react';

import { PrimaryButton } from '@components/Button/variants';
import ConfirmationModal from '@components/ConfirmationModal';
import IconMail from '@components/Images/Rebranding/IconMail';
import {
  LIMIT_REACHED_VERBIAGE,
  SUBSCRIBE_VERBIAGE,
  UNSUBSCRIBE_VERBIAGE,
} from '@components/pages/AI/DeepResearchSettings/AiAlertSubscriptions/constants';
import { MAX_AI_ALERT_SUBSCRIPTIONS_COUNT } from '@components/pages/AI/DeepResearchSettings/AiAlertSubscriptions/constants';
import { NavOption } from '@components/pages/AI/DeepResearchSettings/Nav';
import useAiAlertSubscriptions from '@hooks/useAiAlertSubscriptions';
import useEventTracking from '@hooks/useEventTracking';
import { deep_research_settings_path } from '@routes';
import { ClickTarget, ImpressionElement, click } from '@tracking';

import { VIEW_NOTIFICATION_SETTINGS } from './constants';
import { AiAlertsModalProps } from './interfaces';
import './styles.scss';

const AIAlertsModal = ({
  handleNotificationSubscribeClick,
  handleNotificationUnsubscribeClick,
  isLoading,
  isOpen,
  isSubscribed,
  onClose,
}: AiAlertsModalProps) => {
  const { trackEvent } = useEventTracking();
  const { aiAlertSubscriptions, isLoadingAiAlertSubscriptions } = useAiAlertSubscriptions();

  const aiAlertSubscriptionsCount = aiAlertSubscriptions?.length;
  const currentNotificationUsage = isLoadingAiAlertSubscriptions ? '–' : aiAlertSubscriptionsCount;

  const handleSettingsClick = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.ViewAINotificationSettings,
        clickText: VIEW_NOTIFICATION_SETTINGS,
      })
    );
  };

  const mailIcon = (
    <div className="relative">
      <IconMail className="fill-white dark:fill-black" height={38} width={65} />
      <div
        className={clsx(
          'alert__notification motion-reduce:scale-1 absolute -right-4 flex h-16 w-16 scale-0 items-center justify-center rounded-8 bg-primary-magenta after:font-suisse-intl after:text-[11px] after:text-white',
          {
            '-top-8 after:content-["1"]':
              !isSubscribed && aiAlertSubscriptionsCount < MAX_AI_ALERT_SUBSCRIPTIONS_COUNT,
            'bottom-0 after:pb-2 after:content-["—"]': isSubscribed,
            '-top-8 after:content-["!"]':
              !isSubscribed && aiAlertSubscriptionsCount >= MAX_AI_ALERT_SUBSCRIPTIONS_COUNT,
          }
        )}
      />
    </div>
  );

  if (isSubscribed) {
    return (
      <ConfirmationModal
        additionalText={
          <>
            Notification usage:{' '}
            <span className="text-primary-magenta">
              {currentNotificationUsage}/{MAX_AI_ALERT_SUBSCRIPTIONS_COUNT}
            </span>
          </>
        }
        cancelClickTarget={ClickTarget.Cancel}
        cancelText={UNSUBSCRIBE_VERBIAGE.cancelText}
        confirmClickTarget={ClickTarget.UnsubscribeAiAlertSubscription}
        confirmingText={UNSUBSCRIBE_VERBIAGE.confirmingText}
        confirmText={UNSUBSCRIBE_VERBIAGE.confirmText}
        description={UNSUBSCRIBE_VERBIAGE.bodyText}
        impressionElement={ImpressionElement.AIAlertSubscriptionsExceededModal}
        isLoading={isLoading}
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={handleNotificationUnsubscribeClick}
        rightIcon={mailIcon}
        title={UNSUBSCRIBE_VERBIAGE.headerText}
      />
    );
  }

  if (aiAlertSubscriptionsCount >= MAX_AI_ALERT_SUBSCRIPTIONS_COUNT) {
    const customButton = (
      <PrimaryButton
        as="link"
        className="mt-32"
        href={deep_research_settings_path({ anchor: NavOption.EmailNotifications })}
        onClickCapture={handleSettingsClick}
        fullWidth
      >
        {VIEW_NOTIFICATION_SETTINGS}
      </PrimaryButton>
    );

    return (
      <ConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        title={LIMIT_REACHED_VERBIAGE.headerText}
        description={LIMIT_REACHED_VERBIAGE.bodyText}
        confirmText={LIMIT_REACHED_VERBIAGE.confirmText}
        onConfirm={() => undefined}
        hideCancel={true}
        rightIcon={mailIcon}
        customButton={customButton}
        additionalText={
          <>
            Notification usage:{' '}
            <span className="text-primary-magenta">
              {currentNotificationUsage}/{MAX_AI_ALERT_SUBSCRIPTIONS_COUNT}
            </span>
          </>
        }
        impressionElement={ImpressionElement.AIAlertSubscriptionsExceededModal}
      />
    );
  }

  return (
    <ConfirmationModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      title={SUBSCRIBE_VERBIAGE.headerText}
      description={SUBSCRIBE_VERBIAGE.bodyText}
      confirmText={SUBSCRIBE_VERBIAGE.confirmText}
      confirmingText={SUBSCRIBE_VERBIAGE.confirmingText}
      cancelText={SUBSCRIBE_VERBIAGE.cancelText}
      onConfirm={handleNotificationSubscribeClick}
      rightIcon={mailIcon}
      additionalText={
        <>
          Notification usage:{' '}
          <span className="text-primary-magenta">
            {currentNotificationUsage}/{MAX_AI_ALERT_SUBSCRIPTIONS_COUNT}
          </span>
        </>
      }
      impressionElement={ImpressionElement.AIAlertSubscriptionsExceededModal}
      confirmClickTarget={ClickTarget.SubscribeAiAlertSubscription}
      cancelClickTarget={ClickTarget.Cancel}
    />
  );
};

export default AIAlertsModal;
